import Icon from 'components/Icon';
import { ICONS } from 'consts/consts';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  PopoverBody,
  UncontrolledPopover,
} from 'reactstrap';

import { type CustomSurveyQuestionStatus } from './consts';

interface Props {
  questionId: number;
  isOwnerOrAdmin: boolean;
  status: CustomSurveyQuestionStatus;
  hasAnswers: boolean;
  onEditClick: (questionId: number) => void;
  onManageAdministratorsClick: (questionId: number) => void;
  onArchiveClick: (questionId: number) => void;
  onSetLiveClick: (questionId: number) => void;
  onDeleteClick: (questionId: number) => void;
}

const CustomSurveyQuestionActionsDropdown: FC<Props> = ({
  questionId,
  isOwnerOrAdmin,
  status,
  hasAnswers,
  onEditClick,
  onManageAdministratorsClick,
  onArchiveClick,
  onSetLiveClick,
  onDeleteClick,
}) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const handleEditClick = useCallback(() => {
    onEditClick(questionId);
  }, [questionId, onEditClick]);

  const handleManageAdministratorsClick = useCallback(() => {
    onManageAdministratorsClick(questionId);
  }, [questionId, onManageAdministratorsClick]);

  const handleArchiveClick = useCallback(() => {
    onArchiveClick(questionId);
  }, [questionId, onArchiveClick]);

  const handleSetLiveClick = useCallback(() => {
    onSetLiveClick(questionId);
  }, [questionId, onSetLiveClick]);

  const handleDeleteClick = useCallback(() => {
    onDeleteClick(questionId);
  }, [questionId, onDeleteClick]);

  return (
    <div className="d-flex align-items-center">
      <button
        className="btn btn-sm btn-light p-1 px-2 me-1"
        onClick={handleEditClick}
        disabled={!isOwnerOrAdmin}
        aria-label={formatMessage({
          id: 'app.views.administration.custom_surveys.questions.actions.edit_aria',
          defaultMessage: 'Edit question',
        })}
      >
        <Icon icon={ICONS.EDIT} />
      </button>

      <Dropdown isOpen={isOpen} toggle={toggle} direction="end">
        <DropdownToggle
          tag="button"
          className="focusable-element"
          aria-label={formatMessage({
            id: 'app.views.administration.custom_surveys.questions.actions.aria_label',
            defaultMessage: 'More actions',
          })}
        >
          <span className="btn btn-sm btn-light p-1 px-2">
            <Icon icon={ICONS.MORE_VERTICAL} />
          </span>
        </DropdownToggle>
        <DropdownMenu className="pt-2 pb-2" end>
          {status === 'ACTIVE' ? (
            <DropdownItem
              onClick={handleArchiveClick}
              disabled={!isOwnerOrAdmin}
            >
              <Icon className="pe-2" icon={ICONS.ARCHIVE} />
              <FormattedMessage
                id="app.views.administration.custom_surveys.questions.actions.archive"
                defaultMessage="Archive"
              />
            </DropdownItem>
          ) : (
            <DropdownItem
              onClick={handleSetLiveClick}
              disabled={!isOwnerOrAdmin}
            >
              <Icon className="pe-2" icon={ICONS.REFRESH} />
              <FormattedMessage
                id="app.views.administration.custom_surveys.questions.actions.restore"
                defaultMessage="Set live"
              />
            </DropdownItem>
          )}
          <DropdownItem
            onClick={handleManageAdministratorsClick}
            disabled={!isOwnerOrAdmin}
          >
            <Icon className="pe-2" icon={ICONS.ADD_PERSON} />
            <FormattedMessage
              id="app.views.administration.custom_surveys.questions.actions.manage_administrators"
              defaultMessage="Manage administrators"
            />
          </DropdownItem>
          <span id={`delete-question-action-${questionId}`}>
            <DropdownItem
              className="text-danger"
              onClick={handleDeleteClick}
              disabled={!isOwnerOrAdmin || hasAnswers}
            >
              <Icon className="pe-2" icon={ICONS.TRASH} />
              <FormattedMessage
                id="app.views.administration.custom_surveys.questions.actions.delete"
                defaultMessage="Delete"
              />
            </DropdownItem>
          </span>
          {hasAnswers && (
            <UncontrolledPopover
              placement="left"
              trigger="hover"
              target={`delete-question-action-${questionId}`}
            >
              <PopoverBody>
                <FormattedMessage
                  id="app.views.administration.custom_surveys.questions.actions.delete_disabled_tooltip"
                  defaultMessage="Can't delete a question that has answers. Archive it instead."
                />
              </PopoverBody>
            </UncontrolledPopover>
          )}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default React.memo(CustomSurveyQuestionActionsDropdown);
